<template>
  <div
    class="base-input"
    :class="[{ hasIcon, disabled: isDisabled }, baseComputedClass]"
  >
    <label>
      <p>{{ label }}</p>
      <slot name="label" class="slot-label" />
    </label>
    <div class="base-input__content">
      <slot>
        <input
          type="text"
          :name="name"
          :placeholder="placeholder"
          :class="{ hasErrors: errors }"
          :value="walletAddressTag"
          @keyup="
            $emit('inputValueUpdate', { [`${name}`]: $event.target.value })
          "
          @blur="
            $emit('inputValueUpdate', { [`${name}`]: $event.target.value })
          "
        />
      </slot>
      <p v-if="errors" class="error">{{ errors }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, useSlots } from "vue";

const slots = useSlots();
const props = defineProps({
  name: {
    type: String,
    default: "baseInput",
  },
  label: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: null,
  },
  errors: {
    type: String,
    default: null,
  },
  hasIcon: {
    type: Boolean,
    default: false,
  },
  iconInset: {
    type: Boolean,
    default: true,
  },
});

const getIconInsetClass = props.iconInset
  ? "hasIcon--inset-left"
  : "hasIcon--inset-right";

const baseComputedClass = computed(() =>
  props.hasIcon ? getIconInsetClass : ""
);

const disableByInputName = inject("isDisabledByKey");
const isDisabled = computed(
  () =>
    !!disableByInputName.value &&
    Object.getOwnPropertyDescriptor(disableByInputName.value, props.name) &&
    disableByInputName.value[props.name]
);
</script>
<style lang="scss">
.base-input {
  display: flex;
  flex-direction: column;
  margin-bottom: $formFieldSpacing;
  position: relative;

  input {
    padding: 0 2rem;
  }

  label {
    margin-bottom: 0.5rem;
    margin-left: 0.8rem;
    @include breakpoint($min-m) {
      margin-left: 1.6rem;
    }
  }

  &__content {
    position: relative;
  }

  &.hasIcon {
    input {
      // add +10px to left to the svg's tag's left px
      padding-left: calc(2rem + 40px);
    }
    svg {
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
    }
    &--inset-right {
      svg {
        right: 30px;
      }
    }
    &--inset-left {
      svg {
        left: 30px;
      }
    }
  }

  .caret-down {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .error {
    position: absolute;
    bottom: 0;
    transform: translateY(110%);
    color: var(--error-color);
    font-size: 1.2rem;
  }

  input {
    width: 100%;
    padding: 1.75rem;
    background-color: var(--input-background);
    border-radius: 1.5rem;
    border: 1px solid #0072ce00;
    transition: border 250ms;
    font-size: $font-input-size;

    &:focus,
    &.isFocused {
      border: 1px solid #0072ce;
    }

    &.hasErrors {
      outline: 1px solid var(--error-color);
    }

    &#taxId.hasErrors {
      margin-bottom: 1.3rem;
    }

    @include breakpoint($min-m) {
      font-size: $font-body;
    }
  }

  #taxId.hasErrors + .error {
    bottom: 12px;
  }

  ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: var(--input-place-holder-text-color);
    opacity: 1; /* Firefox */
  }

  :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: var(--input-place-holder-text-color);
  }

  ::-ms-input-placeholder {
    /* Microsoft Edge */
    color: var(--input-place-holder-text-color);
  }
}
</style>
